import React from "react"
import {
    Box,
    ExternalLink,
    HorizontalStack,
    SIZES,
    Text,
    componentStyles,
    useColors,
} from "@mallardbay/lib-react-components"
import { faLink } from "@fortawesome/free-solid-svg-icons/faLink"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import type { UIMessage } from "@ai-sdk/react"

import COPY from "~config/copy-constants"
import { TEST_IDS } from "~config/constants"

const SOURCE_URL_PART_TYPE = "source-url"
const SOURCE_DOCUMENT_PART_TYPE = "source-document"

interface Props {
    readonly part: UIMessage["parts"][number]
}

export default function ChatMessageSource({ part }: Props) {
    const styles = useStyles()
    const source = extractSource(part)
    if (!source) return null

    return (
        <Box style={styles.root} testId={TEST_IDS.CHAT_MESSAGE_SOURCE}>
            <HorizontalStack style={styles.header}>
                <FontAwesomeIcon icon={faLink} />
                <Text style={styles.label}>{COPY.AI_CHAT_SOURCE_LABEL}</Text>
            </HorizontalStack>
            {source.url ? (
                <ExternalLink href={source.url} isUnderlined>
                    {source.title}
                </ExternalLink>
            ) : (
                <Text>{source.title}</Text>
            )}
        </Box>
    )
}

function extractSource(
    part: UIMessage["parts"][number]
): { title: string; url?: string } | null {
    if (part.type === SOURCE_URL_PART_TYPE) {
        return { url: part.url, title: part.title || part.url }
    }
    if (part.type === SOURCE_DOCUMENT_PART_TYPE) {
        return {
            title:
                part.title ||
                part.filename ||
                COPY.AI_CHAT_SOURCE_DOCUMENT_FALLBACK_TITLE,
        }
    }
    return null
}

function useStyles() {
    const colors = useColors()

    return componentStyles({
        root: {
            borderRadius: "md",
            paddingX: 3,
            paddingY: 2,
            borderWidth: "1px",
            borderStyle: "solid",
            borderColor: colors.border,
            fontSize: SIZES.SM,
            gap: 1,
        },
        header: {
            alignItems: "center",
            gap: 2,
            color: colors.secondaryText,
        },
        label: { fontSize: SIZES.SM, fontWeight: "medium" },
    })
}
